// ShadeReport.js

import { POLYGONS } from "../ThreeJs/Global/variables";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import empLogo from "../assets/emp-logo.png";

export async function generateShadeReport(currentDesign, reportPayload) {
  // ---------- Robust getters for dynamic design shapes ----------
  const pickFirstNonEmpty = (...vals) => {
    for (const v of vals) {
      if (typeof v === "string" && v.trim()) return v.trim();
      if (typeof v === "number" && Number.isFinite(v)) return String(v);
    }
    return "";
  };

  const nameFrom = (obj) => {
    if (!obj) return "";
    if (typeof obj === "string") return obj.trim();
    return pickFirstNonEmpty(
      obj.name,
      obj.fullName,
      obj.full_name,
      obj.companyName,
      obj.company_name,
      obj.title
    );
  };

  const getCustomerName = (design) =>
    pickFirstNonEmpty(
      nameFrom(design?.customer),
      design?.customerName,
      design?.customer_name,
      design?.clientName,
      design?.client_name,
      nameFrom(design?.client)
    ) || "N/A";

  const getDesignerName = (design) =>
    pickFirstNonEmpty(
      nameFrom(design?.designer),
      design?.designerName,
      design?.designer_name,
      nameFrom(design?.createdBy),
      design?.created_by_name,
      design?.createdByName
    ) || "N/A";

  const getOrganizationName = (design) =>
    pickFirstNonEmpty(
      nameFrom(design?.organization),
      design?.organizationName,
      design?.organization_name,
      nameFrom(design?.org),
      design?.orgName,
      design?.org_name
    ) || "N/A";

  // ---------- date format ----------
  const now = new Date();
  const formattedDateTime = (() => {
    const datePart = new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(now);

    const timePart = new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(now);

    return `${datePart} - ${timePart}`;
  })();

  // ---------- HELPERS FOR PERCENTAGE FORMATTING ----------
  function formatTwoSigFigs(value) {
    if (!isFinite(value) || value === 0) return "0";
    const abs = Math.abs(value);
    const digits = Math.floor(Math.log10(abs)) + 1;
    const decimals = Math.max(0, 2 - digits);
    return value.toFixed(decimals);
  }

  function formatPercentTwoSigFigs(ratio) {
    const percent = ratio * 100;
    return `${formatTwoSigFigs(percent)}%`;
  }

  // ---------- SUMMARY DATA FROM POLYGONS ----------
  const summary =
    POLYGONS?.filter((p) => (p?.panels?.length || 0) > 0).map((polygon, index) => {
      const panels = polygon?.panels?.length || 0;
      const asa = polygon?.irradiance?.ASA || 0;
      const tof = polygon?.irradiance?.TOF || 0;
      const tsrf = asa * tof;

      return {
        id: index + 1,
        panels,
        azimuth: polygon.details?.azimu ?? "-",
        pitch: polygon.details?.pitch ?? "-",
        tofDisplay: formatPercentTwoSigFigs(tof),
        solarAccessDisplay: formatPercentTwoSigFigs(asa),
        tsrfDisplay: formatPercentTwoSigFigs(tsrf),
        asa,
        tof,
        tsrf,
      };
    }) || [];

  const totalPanels = summary.reduce((sum, r) => sum + r.panels, 0);

  const weightedASA =
    totalPanels > 0
      ? summary.reduce((sum, r) => sum + r.asa * r.panels, 0) / totalPanels
      : 0;

  const weightedTSRF =
    totalPanels > 0
      ? summary.reduce((sum, r) => sum + r.tsrf * r.panels, 0) / totalPanels
      : 0;

  function getMonthlySolarAccess(polygons) {
    if (!Array.isArray(polygons)) return [];
    return polygons
      .filter((poly) => (poly?.panels?.length || 0) > 0)
      .map((poly) => {
        const monthlyASA = poly?.irradiance?.monthlyASA || [];
        return monthlyASA.map((m) => Math.round((m.asa || 0) * 100));
      });
  }

  const monthlySolarAccess = getMonthlySolarAccess(POLYGONS);
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  // ---------- DESIGN META ----------
  const customerName = getCustomerName(currentDesign);
  const designerName = getDesignerName(currentDesign);
  const organizationName = getOrganizationName(currentDesign);

  const data = {
    title: `Shade Report - ${currentDesign?.name || "Untitled Design"}`,
    address: currentDesign?.address || "Address not available",
    coordinates:
      currentDesign?.latitude != null && currentDesign?.longitude != null
        ? `${currentDesign.latitude}, ${currentDesign.longitude}`
        : "N/A",
    dateTime: formattedDateTime,
    summary,
    weighted: {
      asa: `${(weightedASA * 100).toFixed(1)}%`,
      tsrf: `${(weightedTSRF * 100).toFixed(1)}%`,
    },
    monthlySolarAccess,
    initialSnapshot: reportPayload?.snapshot || "",
  };

  // ---------- PDF GENERATOR ----------
  const generateShadePdf = async ({ snapshotDataUrl }) => {
    const doc = new jsPDF("p", "pt", "letter");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const leftMargin = 36;
    const rightMargin = 36;
    const contentWidth = pageWidth - leftMargin - rightMargin;

    const addHeaderLogo = () => {
      const logoW = 110;
      const logoH = (logoW * 173) / 975;
      const logoX = leftMargin;
      const logoY = 16;

      try {
        doc.addImage(empLogo, "PNG", logoX, logoY, logoW, logoH);
      } catch (e) {}

      return { logoX, logoY, logoW, logoH };
    };

    const addFooter = (pageIndex, pageCount) => {
      const pw = doc.internal.pageSize.getWidth();
      const ph = doc.internal.pageSize.getHeight();

      const footerY = ph - 26;
      const footerLineY = footerY - 26;

      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(1.6);
      doc.line(leftMargin, footerLineY, pw - rightMargin, footerLineY);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      const footerNote = "All rights reserved © Empower Energy Solutions.";
      const wrapped = doc.splitTextToSize(footerNote, pw - leftMargin - rightMargin);
      doc.text(wrapped, leftMargin, footerLineY + 14);

      doc.setFontSize(8);
      doc.text(`${pageIndex}/${pageCount}`, pw - rightMargin, footerY, { align: "right" });
    };

    const ensureSpace = (y, minSpace = 120) => {
      const bottomSafe = 90;
      if (y + minSpace > pageHeight - bottomSafe) {
        doc.addPage();
        return 60;
      }
      return y;
    };

    // ---------- PAGE 1 HEADER ----------
    const { logoX, logoY, logoH } = addHeaderLogo();

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(data.dateTime, logoX, logoY + logoH + 14);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(data.title, pageWidth / 2, 44, { align: "center" });

    let y = 90;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);

    const metaLine = (label, value) => {
      doc.setFont("helvetica", "normal");
      doc.text(label, leftMargin, y);
      doc.setFont("helvetica", "bold");
      doc.text(value || "N/A", leftMargin + 120, y);
      y += 14;
    };

    metaLine("Address:", data.address);
    metaLine("Coordinates:", data.coordinates);
    y += 10;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Annual Irradiance", leftMargin, y);
    y += 10;

    if (snapshotDataUrl && typeof snapshotDataUrl === "string" && snapshotDataUrl.startsWith("data:image/")) {
      y += 8;
      const imgW = contentWidth;
      const imgH = imgW * 0.56;
      y = ensureSpace(y, imgH + 30);

      try {
        doc.addImage(snapshotDataUrl, "JPEG", leftMargin, y, imgW, imgH);
        y += imgH + 18;
      } catch (e) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text("Snapshot could not be embedded.", leftMargin, y);
        y += 16;
      }
    } else {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text("Snapshot not available.", leftMargin, y + 12);
      y += 24;
    }

    y = ensureSpace(y, 220);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Summary", leftMargin, y);
    y += 8;

    const summaryRows = (data.summary || []).map((r) => [
      String(r.id),
      String(r.panels),
      String(r.azimuth),
      String(r.pitch),
      String(r.tofDisplay),
      String(r.solarAccessDisplay),
      String(r.tsrfDisplay),
    ]);

    autoTable(doc, {
      startY: y + 6,
      margin: { left: leftMargin, right: rightMargin },
      theme: "grid",
      head: [["Array ID","Panel Count","Azimuth","Pitch","TOF (%)","Annual Solar Access (%)","Annual TSRF (%)"]],
      body: summaryRows,
      styles: { font: "helvetica", fontSize: 9, cellPadding: 4, valign: "middle" },
      headStyles: { fillColor: [245,245,245], textColor: [0,0,0], fontStyle: "bold", halign: "center" },
      bodyStyles: { halign: "center" },
      didDrawPage: () => addHeaderLogo(),
    });

    let afterSummaryY = doc.lastAutoTable.finalY + 10;
    afterSummaryY = ensureSpace(afterSummaryY, 40);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Weighted average by panel count:", leftMargin, afterSummaryY);
    doc.text(`ASA: ${data.weighted.asa}`, leftMargin + 210, afterSummaryY);
    doc.text(`TSRF: ${data.weighted.tsrf}`, leftMargin + 330, afterSummaryY);

    y = afterSummaryY + 34;

    y = ensureSpace(y, 260);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Monthly Solar Access % Across Arrays", leftMargin, y);
    y += 10;

    const monthlyHead = ["Array ID", ...months];
    const monthlyRows = (data.monthlySolarAccess || []).map((row, i) => [
      String(i + 1),
      ...(row || []).map((v) => String(v)),
    ]);

    autoTable(doc, {
      startY: y + 6,
      margin: { left: leftMargin, right: rightMargin },
      theme: "grid",
      head: [monthlyHead],
      body: monthlyRows,
      styles: { font: "helvetica", fontSize: 8, cellPadding: 3, valign: "middle" },
      headStyles: { fillColor: [245,245,245], textColor: [0,0,0], fontStyle: "bold", halign: "center" },
      bodyStyles: { halign: "center" },
      didDrawPage: () => addHeaderLogo(),
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      addHeaderLogo();
      addFooter(i, pageCount);
    }

    doc.save(`Shade_Report_${currentDesign?.name || "Untitled"}.pdf`);
  };

  // ---------- Use the already-open popup window ----------
  const page = reportPayload?.__reportWindowRef || window.open("", "_blank");
  if (!page) {
    alert("Popup blocked. Please allow popups for this site to view the report.");
    return;
  }

  // Listen for snapshot messages from popup (PDF trigger)
  function onSnapshotMsg(event) {
    if (event?.data?.type === "SHADE_REPORT_SNAPSHOT") {
      const snapshotDataUrl = event?.data?.snapshot;
      generateShadePdf({ snapshotDataUrl });
    }
  }
  window.addEventListener("message", onSnapshotMsg);

  // Ready promise
  const readyPromise = new Promise((resolve) => {
    function handleMessage(event) {
      if (event?.data?.type === "SHADE_REPORT_READY") {
        window.removeEventListener("message", handleMessage);
        resolve();
      }
    }
    window.addEventListener("message", handleMessage);
  });

  // ---------- HTML (no scene payload embedded) ----------
  page.document.write(`
    <html>
      <head>
        <title>${data.title}</title>
        <meta charset="utf-8" />
        <style>
          body {
            font-family: "Inter", Arial, sans-serif;
            background: #fff;
            color: #111;
            margin: 40px;
          }
          .header { margin-bottom: 20px; }
          h1 { font-size: 22px; margin-bottom: 5px; }
          h2 { font-size: 18px; margin: 24px 0 12px; }
          .info-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 8px 20px;
            font-size: 14px;
            color: #444;
          }
          .label { font-weight: 600; color: #222; }
          .section { margin-top: 30px; }

          .image-wrapper {
            position: relative;
            display: block;
            margin-top: 10px;
            margin-bottom: 10px;
            width: 80%;
            max-width: 900px;
            margin-left: auto;
            margin-right: auto;
          }

          /* ✅ FIX: consistent snapshot aspect ratio (16:9) */
          .snapshot-frame {
            position: relative;
            width: 100%;
            aspect-ratio: 16 / 9;
            background: #f3f3f3;
            border-radius: 4px;
            overflow: hidden;
          }
          #irradiance-image {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            object-fit: cover; /* change to contain if you want no cropping */
            display: block;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
            font-size: 14px;
          }
          th, td {
            border: 1px solid #e0e0e0;
            padding: 8px;
            text-align: center;
          }
          th {
            background: #f7f7f7;
            font-weight: 600;
          }
          .footer-row {
            text-align: right;
            font-weight: 600;
            color: #333;
          }

          .edit-view-btn {
            position: absolute;
            top: 12px;
            right: 12px;
            width: 36px;
            height: 36px;
            padding: 0 0 0 0;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 4px;
            border: 1px solid #d0d0d0;
            background: #e0e0e0;
            color: #333;
            cursor: pointer;
          }
          .edit-view-btn:disabled { opacity: 0.6; cursor: default; }

          .edit-view-btn.loading::after {
            content: "";
            width: 14px;
            height: 14px;
            border: 2px solid rgba(0,0,0,0.25);
            border-top-color: rgba(0,0,0,0.8);
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
            margin-left: 6px;
          }
          @keyframes spin { to { transform: rotate(360deg); } }

          .edit-icon { width: 18px; height: 18px; fill: currentColor; }

          #viewer-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 9999;
            opacity: 0;
            pointer-events: none;
            transition: opacity 0.25s ease-out;
          }
          #viewer-overlay.show { opacity: 1; pointer-events: auto; }

          #viewer-container {
            width: 800px;
            max-width: 100%;
            height: 450px;
            background: #111;
            border-radius: 10px;
            box-shadow: 0 18px 45px rgba(0,0,0,0.45);
            border: 1px solid rgba(255,255,255,0.12);
            position: relative;
            overflow: hidden;
          }

          #viewer-overlay.show #viewer-container { animation: slideUpFade 0.3s ease-out; }

          @keyframes slideUpFade {
            from { transform: translateY(40px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }

          #viewer-canvas { width: 100%; height: 100%; display: block; }
          #viewer-controls { position: absolute; bottom: 12px; right: 12px; display:flex; gap: 8px; }

          .viewer-btn {
            padding: 8px 14px;
            font-size: 13px;
            border-radius: 4px;
            border: 1px solid #d0d0d0;
            background: #1890ff;
            color: #fff;
            cursor: pointer;
            min-width: 80px;
          }
          .viewer-btn:hover { background: #1677c3; }
          .viewer-btn:disabled { opacity: 0.6; cursor: default; }

          #sun-toggle-btn {
            position: absolute;
            top: 12px;
            right: 12px;
            width: 36px;
            height: 36px;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            border: 1px solid #f0c14b;
            background: #ffe58f;
            color: #b37f00;
            cursor: pointer;
            z-index: 2;
          }
          #sun-toggle-btn .sun-icon { width: 18px; height: 18px; fill: currentColor; }
          #sun-toggle-btn.off { background: #f0f0f0; border-color: #d9d9d9; color: #999; }

          .download-btn-container {
            position: fixed;
            top: 16px;
            right: 16px;
            z-index: 1000;
          }
          #download-pdf-btn {
            padding: 8px 14px;
            font-size: 13px;
            border-radius: 4px;
            border: 1px solid #d0d0d0;
            background: #1890ff;
            color: #fff;
            cursor: pointer;
          }
          #download-pdf-btn:hover { background: #1677c3; }

          #viewer-loading {
            position: absolute;
            inset: 0;
            display: none;
            align-items: center;
            justify-content: center;
            color: #fff;
            font-family: "Inter", Arial, sans-serif;
            font-size: 14px;
            background: rgba(0,0,0,0.35);
            z-index: 3;
          }
          #viewer-loading.show { display: flex; }
          .spinner {
            width: 18px;
            height: 18px;
            border: 2px solid rgba(255,255,255,0.4);
            border-top-color: rgba(255,255,255,0.95);
            border-radius: 50%;
            animation: spin2 0.8s linear infinite;
            margin-right: 10px;
          }
          @keyframes spin2 { to { transform: rotate(360deg); } }
        </style>
      </head>
      <body>
        <div class="download-btn-container">
          <button id="download-pdf-btn" type="button">Download PDF</button>
        </div>

        <div class="header">
          <h1>${data.title}</h1>
          <div class="info-grid">
            <div><span class="label">Address</span><br>${data.address}</div>
            <div><span class="label">Coordinates</span><br>${data.coordinates}</div>
            <div><span class="label">Date</span><br>${data.dateTime}</div>
          </div>
        </div>

        <div class="section">
          <h2>Annual irradiance</h2>
          <div class="image-wrapper">
            <div class="snapshot-frame">
              <img id="irradiance-image" src="${data.initialSnapshot || ""}" alt="Annual Irradiance Snapshot" />
            </div>
            <button id="edit-view-btn" class="edit-view-btn" title="Edit View" aria-label="Edit View">
              <svg class="edit-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"></path>
              </svg>
            </button>
          </div>
        </div>

        <div class="section">
          <h2>Summary</h2>
          <table>
            <thead>
              <tr>
                <th>Array ID</th>
                <th>Panel count</th>
                <th>Azimuth</th>
                <th>Pitch</th>
                <th>TOF (%)</th>
                <th>Annual Solar Access (%)</th>
                <th>Annual TSRF (%)</th>
              </tr>
            </thead>
            <tbody>
              ${data.summary.map((r) => `
                <tr>
                  <td>${r.id}</td>
                  <td>${r.panels}</td>
                  <td>${r.azimuth}</td>
                  <td>${r.pitch}</td>
                  <td>${r.tofDisplay}</td>
                  <td>${r.solarAccessDisplay}</td>
                  <td>${r.tsrfDisplay}</td>
                </tr>
              `).join("")}
              <tr class="footer-row">
                <td colspan="5">Weighted average by panel count:</td>
                <td>${data.weighted.asa}</td>
                <td>${data.weighted.tsrf}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="section">
          <h2>Monthly solar access % across arrays</h2>
          <table>
            <thead>
              <tr>
                <th>Array ID</th>
                ${months.map((m) => `<th>${m}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${data.monthlySolarAccess.map((row, i) => `
                <tr>
                  <td>${i + 1}</td>
                  ${(row || []).map((val) => `<td>${val}</td>`).join("")}
                </tr>
              `).join("")}
            </tbody>
          </table>
        </div>

        <div id="viewer-overlay">
          <div id="viewer-container">
            <div id="viewer-loading">
              <div class="spinner"></div>
              Loading 3D view...
            </div>

            <button id="sun-toggle-btn" title="Toggle Irradiance" aria-label="Toggle Irradiance" disabled>
              <svg class="sun-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z"></path>
              </svg>
            </button>

            <canvas id="viewer-canvas"></canvas>
            <div id="viewer-controls">
              <button id="viewer-done-btn" class="viewer-btn" disabled>Done</button>
              <button id="viewer-cancel-btn" class="viewer-btn">Cancel</button>
            </div>
          </div>
        </div>

        <script>
          (function () {
            var openerWin = window.opener;

            // ✅ Tell opener we're ready immediately
            if (openerWin && openerWin.postMessage) {
              openerWin.postMessage({ type: "SHADE_REPORT_READY" }, "*");
            }

            var imgEl = document.getElementById("irradiance-image");
            var editBtn = document.getElementById("edit-view-btn");

            var overlay = document.getElementById("viewer-overlay");
            var canvas = document.getElementById("viewer-canvas");
            var loadingEl = document.getElementById("viewer-loading");
            var sunBtn = document.getElementById("sun-toggle-btn");
            var doneBtn = document.getElementById("viewer-done-btn");
            var cancelBtn = document.getElementById("viewer-cancel-btn");

            var THREE = openerWin && openerWin.THREE;
            var OrbitControlsCtor = openerWin && openerWin.OrbitControls;

            var renderer = null;
            var scene = null;
            var camera = null;
            var controls = null;

            var sceneLoaded = false;
            var sceneRequested = false;

            // ✅ NEW: if user clicks Edit View while still loading, auto-open overlay when ready
            var openOverlayWhenReady = false;

            var IRRADIANCE_SPRITE_NAME = "IRRADIANCE_SPRITE";
            var sunOn = true;

            function showViewerLoading(on) {
              if (!loadingEl) return;
              loadingEl.classList.toggle("show", !!on);
            }

            function setButtonsEnabled(enabled) {
              if (sunBtn) sunBtn.disabled = !enabled;
              if (doneBtn) doneBtn.disabled = !enabled;
            }

            function updateSize() {
              if (!renderer || !camera) return;
              var w = canvas.clientWidth || 800;
              var h = canvas.clientHeight || 450;
              if (w <= 0 || h <= 0) return;
              renderer.setSize(w, h, false);
              camera.aspect = w / h;
              camera.updateProjectionMatrix();
            }

            function setIrradianceVisibility(visible) {
              if (!scene) return;
              scene.traverse(function (obj) {
                if (obj && obj.type === "Sprite" && obj.name === IRRADIANCE_SPRITE_NAME) {
                  obj.visible = visible;
                }
              });
            }

            function initViewer(payload) {
              if (!canvas || !THREE || !OrbitControlsCtor) {
                console.warn("Viewer dependencies not available.");
                return;
              }

              renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true });
              renderer.setPixelRatio(window.devicePixelRatio || 1);

              scene = new THREE.Scene();
              var loader = new THREE.ObjectLoader();

              try {
                var loadedScene = loader.parse(payload.scene);
                scene.add(loadedScene);
              } catch (e) {
                console.error("Failed to load scene from payload:", e);
                return;
              }

              var cameraFov = (payload.camera && payload.camera.fov) || 45;
              camera = new THREE.PerspectiveCamera(cameraFov, 800 / 450, 0.1, 5000);
              camera.up.set(0, 0, 1);

              var camPos = (payload.camera && payload.camera.position) || [0, 0, 50];
              camera.position.set(camPos[0], camPos[1], camPos[2]);

              controls = new OrbitControlsCtor(camera, renderer.domElement);
              controls.mouseButtons = {
                LEFT: null,
                MIDDLE: THREE.MOUSE.PAN,
                RIGHT: THREE.MOUSE.ROTATE,
              };

              controls.zoomToCursor = true;
              controls.rotateSpeed = 1;
              controls.enablePan = true;
              controls.maxPolarAngle = Math.PI / 2;

              var camTarget = (payload.camera && payload.camera.target) || [0, 0, 0];
              controls.target.set(camTarget[0], camTarget[1], camTarget[2]);
              controls.update();

              var ambient = new THREE.AmbientLight(0xffffff, 0.6);
              scene.add(ambient);

              var dir = new THREE.DirectionalLight(0xffffff, 0.8);
              dir.position.set(5, 10, 7);
              scene.add(dir);

              function animate() {
                requestAnimationFrame(animate);
                if (controls) controls.update();
                if (renderer && scene && camera) renderer.render(scene, camera);
              }

              updateSize();
              animate();

              setIrradianceVisibility(true);
              sunOn = true;
              if (sunBtn) sunBtn.classList.remove("off");

              sceneLoaded = true;
              showViewerLoading(false);
              setButtonsEnabled(true);

              // ✅ scene ready: re-enable edit button, remove spinner
              if (editBtn) {
                editBtn.disabled = false;
                editBtn.title = "Edit View";
                editBtn.classList.remove("loading");
              }

              // ✅ NEW: auto-open overlay if user clicked while loading
              if (openOverlayWhenReady && overlay) {
                openOverlayWhenReady = false;
                overlay.classList.add("show");
                updateSize();
              }
            }

            function captureSnapshotFromViewer() {
              if (!renderer) return "";
              updateSize();
              renderer.render(scene, camera);
              try {
                return renderer.domElement.toDataURL("image/jpeg", 1.0);
              } catch (e) {
                return "";
              }
            }

            // ✅ Edit View:
            // First click -> background load only (page remains scrollable)
            // If clicked while loading -> remember and auto-open when ready
            if (editBtn) {
              editBtn.addEventListener("click", function () {
                if (!sceneLoaded) {
                  // user intent: open overlay when ready
                  openOverlayWhenReady = true;

                  if (!sceneRequested) {
                    sceneRequested = true;

                    // show loading state on button
                    editBtn.disabled = true;
                    editBtn.title = "Preparing 3D view...";
                    editBtn.classList.add("loading");

                    if (openerWin && openerWin.postMessage) {
                      openerWin.postMessage({ type: "REQUEST_SHADE_REPORT_SCENE" }, "*");
                    }
                  }
                  return;
                }

                if (sceneLoaded && overlay) {
                  overlay.classList.add("show");
                  updateSize();
                }
              });
            }

            if (doneBtn && overlay) {
              doneBtn.addEventListener("click", function () {
                var snap = captureSnapshotFromViewer();
                if (snap && imgEl) imgEl.src = snap;
                overlay.classList.remove("show");
              });
            }

            if (cancelBtn && overlay) {
              cancelBtn.addEventListener("click", function () {
                overlay.classList.remove("show");
              });
            }

            if (sunBtn) {
              sunBtn.addEventListener("click", function () {
                if (!sceneLoaded) return;
                sunOn = !sunOn;
                setIrradianceVisibility(sunOn);
                sunBtn.classList.toggle("off", !sunOn);
              });
            }

            // ✅ Receive messages:
            window.addEventListener("message", function (event) {
              if (event?.data?.type === "SHADE_REPORT_SCENE_DATA") {
                try {
                  showViewerLoading(true);
                  initViewer(event.data.payload);
                } catch (e) {
                  console.error("initViewer failed:", e);
                  showViewerLoading(false);
                  setButtonsEnabled(false);
                }
              }

              if (event?.data?.type === "SHADE_REPORT_SCENE_ERROR") {
                console.error("Scene export error:", event.data.error);
                showViewerLoading(false);
                setButtonsEnabled(false);
                if (editBtn) {
                  editBtn.disabled = false;
                  editBtn.title = "Edit View (failed)";
                  editBtn.classList.remove("loading");
                }
              }

              // ✅ Fallback snapshot updater from opener
              if (event?.data?.type === "SHADE_REPORT_SET_SNAPSHOT") {
                var s = event.data.snapshot;
                if (imgEl && s && typeof s === "string" && s.startsWith("data:image/")) {
                  imgEl.src = s;
                }
              }
            });

            // ✅ Download PDF: use viewer snapshot if available else current image
            var downloadBtn = document.getElementById("download-pdf-btn");
            if (downloadBtn) {
              downloadBtn.addEventListener("click", function () {
                var snap = "";
                if (sceneLoaded) snap = captureSnapshotFromViewer();
                if (!snap && imgEl && imgEl.src) snap = imgEl.src;

                if (openerWin && openerWin.postMessage) {
                  openerWin.postMessage({ type: "SHADE_REPORT_SNAPSHOT", snapshot: snap }, "*");
                }
              });
            }

            window.addEventListener("resize", updateSize);
          })();
        </script>
      </body>
    </html>
  `);

  page.document.close();
  await readyPromise;
}